import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { CaseFormModal } from "../components/CaseFormModal";
import { CustomerFormModal } from "../components/CustomerFormModal";
import { DashboardHome } from "../components/DashboardHome";
import { TaskFormModal } from "../components/TaskFormModal";
import {
  Button,
  Card,
  EmptyState as UiEmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from "../components/ui";
import { useToast } from "../hooks/useToast";
import { ApiError, type ApiTask, createTask } from "../lib/api";

export function TodayPage(): JSX.Element {
  const [creatingCase, setCreatingCase] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const queryClient = useQueryClient();
  return (
    <div className="space-y-6">
      <PageHeader
        title="今日やること / ダッシュボード"
        actions={
          <TodayHeaderActions
            onCreateCase={() => setCreatingCase(true)}
            onCreateCustomer={() => setCreatingCustomer(true)}
            onCreateTask={() => setCreatingTask(true)}
          />
        }
      />
      <DashboardHome />
      {creatingCase && <TodayCaseModal onClose={() => setCreatingCase(false)} queryClient={queryClient} />}
      {creatingCustomer && (
        <TodayCustomerModal onClose={() => setCreatingCustomer(false)} queryClient={queryClient} />
      )}
      {creatingTask && <TaskFormModal mode="create" onClose={() => setCreatingTask(false)} />}
    </div>
  );
}

function TodayHeaderActions({
  onCreateCase,
  onCreateCustomer,
  onCreateTask,
}: {
  onCreateCase: () => void;
  onCreateCustomer: () => void;
  onCreateTask: () => void;
}): JSX.Element {
  return (
    <>
      <Button variant="secondary" size="sm" onClick={onCreateCustomer}>
        + 顧客
      </Button>
      <Button variant="secondary" size="sm" onClick={onCreateTask}>
        + タスク
      </Button>
      <Button size="sm" onClick={onCreateCase}>
        + 新規案件
      </Button>
    </>
  );
}

function TodayCaseModal({
  onClose,
  queryClient,
}: {
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  return (
    <CaseFormModal
      mode="create"
      onClose={onClose}
      onSuccess={() => {
        void queryClient.invalidateQueries({ queryKey: ["cases"] });
        void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      }}
    />
  );
}

function TodayCustomerModal({
  onClose,
  queryClient,
}: {
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  return (
    <CustomerFormModal
      mode="create"
      onClose={onClose}
      onSuccess={() => {
        void queryClient.invalidateQueries({ queryKey: ["customers"] });
      }}
    />
  );
}

// ─── 共有エクスポート (他ページが利用) ──────────────────────────

export function TaskCreateModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [form, setForm] = useState(() => initialTaskForm());
  const [titleError, setTitleError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () => createTask(buildTaskCreateInput(form)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["today"] });
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("タスクを追加しました");
      onClose();
    },
    onError: () => toast.error("タスクの追加に失敗しました"),
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const error = validateTaskTitle(form.title);
    setTitleError(error);
    if (error === undefined) mutation.mutate();
  };

  return (
    <Modal open title="今日のタスクを追加" onClose={onClose} className="max-w-lg">
      <form onSubmit={submit} className="space-y-4">
        <TaskCreateFields form={form} error={titleError} onChange={setForm} />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            キャンセル
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            追加
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface TaskCreateForm {
  title: string;
  description: string;
  dueDate: string;
  priority: ApiTask["priority"];
}

function TaskCreateFields({
  form,
  error,
  onChange,
}: {
  form: TaskCreateForm;
  error: string | undefined;
  onChange: (form: TaskCreateForm) => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <TaskField label="タイトル" error={error}>
        <Input value={form.title} onChange={(e) => onChange({ ...form, title: e.target.value })} />
      </TaskField>
      <div className="grid gap-3 sm:grid-cols-2">
        <TaskField label="期限日">
          <Input
            type="date"
            value={form.dueDate}
            onChange={(e) => onChange({ ...form, dueDate: e.target.value })}
          />
        </TaskField>
        <TaskField label="優先度">
          <Select
            value={form.priority}
            onChange={(e) => onChange({ ...form, priority: e.target.value as ApiTask["priority"] })}
          >
            <option value="high">高</option>
            <option value="medium">中</option>
            <option value="low">低</option>
          </Select>
        </TaskField>
      </div>
      <TaskField label="説明">
        <Textarea
          value={form.description}
          rows={3}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
        />
      </TaskField>
    </div>
  );
}

function TaskField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="block text-xs font-medium text-text-secondary">
      <span>{label}</span>
      <div className="mt-1">{children}</div>
      {error !== undefined && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

function initialTaskForm(): TaskCreateForm {
  return { title: "", description: "", dueDate: todayDateInputValue(), priority: "medium" };
}

function validateTaskTitle(title: string): string | undefined {
  return title.trim().length === 0 ? "タイトルは必須です" : undefined;
}

function buildTaskCreateInput(form: TaskCreateForm): Parameters<typeof createTask>[0] {
  const input: Parameters<typeof createTask>[0] = {
    title: form.title.trim(),
    priority: form.priority,
    status: "open",
  };
  if (form.dueDate.length > 0) input.due_date = `${form.dueDate}T23:59:59.999+09:00`;
  const description = form.description.trim();
  if (description.length > 0) input.description = description;
  return input;
}

function todayDateInputValue(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function SkeletonList(): JSX.Element {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((i) => (
        <li key={i}>
          <Card className="space-y-3">
            <Skeleton className="h-5 w-3/5" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/5" />
          </Card>
        </li>
      ))}
    </ul>
  );
}

export function ErrorBox({
  error,
  message,
  onRetry,
}: {
  error: unknown;
  message: string;
  onRetry?: () => void;
}): JSX.Element {
  const detail = error instanceof ApiError ? `(${error.code})` : "";
  const messageText = `時間を置いて再読み込みしてください ${detail}`;
  if (onRetry === undefined) {
    return <ErrorState title={message} message={messageText} retryLabel="再試行" />;
  }
  return <ErrorState title={message} message={messageText} retryLabel="再試行" onRetry={onRetry} />;
}

export function EmptyState({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description?: string;
}): JSX.Element {
  const iconNode = <span className="text-3xl">{icon}</span>;
  if (description === undefined) {
    return <UiEmptyState icon={iconNode} title={title} />;
  }
  return <UiEmptyState icon={iconNode} title={title} description={description} />;
}

export function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
